import axios from "axios";
import {TransferApi} from "transfers/contacts/TransferApi";
import {parseApiInstant} from "core/format/dateFormat";
import {responseStatus} from "utils/ErrorUtils";
import type {PersonalizationDraftRequest} from "./personalizationDraft";

export interface SentPersonalizationWindow {
	/**
	 * The transfer's RAW uuid — what {@link attachSentPersonalization} addresses.
	 *
	 * Deliberately not the ENCODED share id the success screen holds: the post-send changes
	 * and this attach are all addressed by the persisted uuid, while the `/transfer/uploads/{id}/…` routes take the
	 * encoded one. The owner's row, read by the encoded id, carries both, so reading the window is also what
	 * resolves the id.
	 */
	transferUuid: string;
	expiresAtMs: number | null;
}

/** Use the owner's persisted expiry, never the success card's upload-finished estimate. */
export async function readSentPersonalizationWindow(transferId: string): Promise<SentPersonalizationWindow | null> {
	// One row by the encoded id the success screen holds, rather than the owner's whole list
	// (kitecorp/transferify-api-service#2163); null when the transfer is gone or not the caller's.
	const transfer = await TransferApi.findListEntry(transferId);
	// `keepOnline` is deliberately NOT asked: it tells hosted storage from a peer-to-peer registration,
	// and both may take a first look since api#1592. What still decides is whether the transfer is
	// sendable at all (`complete`), under review (`suspended`) and inside its own served window.
	if (!transfer || !transfer.complete || transfer.suspended) return null;
	if (transfer.expiresAt === null) return {transferUuid: transfer.id, expiresAtMs: null};
	const expiry = parseApiInstant(transfer.expiresAt);
	return expiry === null ? null : {transferUuid: transfer.id, expiresAtMs: expiry.toMillis()};
}

/**
 * What the attach answered.
 *
 * `closed` is the api's 410: this link can take no further look, because a peer-to-peer transfer's
 * download limit is spent. It is a state of the transfer rather than a failure of the request, so the
 * flow shows its existing "this transfer has expired" notice instead of offering a retry. Every other
 * refusal is rethrown — there is no verdict to give, and the flow reports a failure.
 */
export type SentPersonalizationAttachOutcome = "attached" | "closed";

/**
 * Attaches a look composed AFTER the send to an already-live transfer (api#1592).
 *
 * The dedicated route, not the hosted upload's fileless completion: completion exists only on the
 * hosted path, so attaching through it left a peer-to-peer sender with no way to personalize what they
 * had just sent. The api applies the same funding and write-once rules either way, and an identical
 * draft is a retry rather than a refusal — which is what lets the flow re-send its retained snapshot
 * after an ambiguous transport failure.
 */
export async function attachSentPersonalization(transferUuid: string,
		request: PersonalizationDraftRequest): Promise<SentPersonalizationAttachOutcome> {
	try {
		await axios.post(`/transfer/${transferUuid}/personalization`, request);
		return "attached";
	} catch (error) {
		if (responseStatus(error) === 410) return "closed";
		throw error;
	}
}
